import { ErrorMessage, Field, Form, Formik } from 'formik';
import { useAtom } from 'jotai';
import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { profileAtom } from '../../../jotai-atoms/profile.atom';
import { StudentInformationProps } from '../../../models/Profile';
import { InputFeedback } from '../../../stories/InputFeedback';
import { Modal } from '../../../stories/Modal';
import { Panel } from '../../../stories/Panel';
import { TextInput } from '../../../stories/TextInput';
import { useUpdateStudentInformation } from '../../../hooks/use-update-student-information';

interface StudentInformationModalProps {
  show: boolean;
  onClose: () => void;
}

const StudentInformationModal: FC<StudentInformationModalProps> = (props) => {
  const __ = useTranslation().t;
  const [errorStateActive, setErrorStateActive] = useState<boolean>(false);
  const [profile, setProfile] = useAtom(profileAtom);
  const initialValues = { ...profile?.studentInformation };
  const { mutateAsync, isLoading } = useUpdateStudentInformation()

  const schema = yup.object().shape({
    firstName: yup.string().nullable().required(__('fieldRequired')),
    lastName: yup.string().nullable().required(__('fieldRequired')),
    email: yup.string().email(__('checkYourEmail')).nullable().required(__('fieldRequired')),
  });

  const modalProps = {
    show: props.show,
    type: 'default',
    title: __('studentInfo'),
    submitButtonText: __('save'),
    cancelButtonText: __('cancel'),
  };

  const onSubmit = async (values: StudentInformationProps) => {
    const studentInformationWithToken = {
      ...values,
      _token: profile?._token,
    }
    mutateAsync(studentInformationWithToken, {
      onSuccess: () => {
        setErrorStateActive(false);
        props.onClose();
        setProfile({
          ...profile,
          studentInformation: values,
        });
      },
      onError: () => {
        setErrorStateActive(true);
      }
    });
  };

  const onClose = (resetForm: () => void) => {
    resetForm();
    setErrorStateActive(false);
    props.onClose();
  };

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validationSchema={schema}
      onSubmit={onSubmit}
    >
      {({ submitForm, resetForm }) => (
        <Modal
          {...modalProps}
          submitButtonDisabled={isLoading}
          onClose={() => onClose(resetForm)}
          onSubmit={submitForm}
        >
          <Form className="oejr-profile-modal-form">
            <Panel
              variant="danger"
              hidden={!errorStateActive}
              onHide={() => setErrorStateActive(false)}
            >
              <p>{__('opsCheckTyposContactUs')}</p>
            </Panel>
            <Field
              type="custom_text-letters-only"
              name="firstName"
              label={__('name') + ' *'}
              component={TextInput}
            />
            <ErrorMessage
              name="firstName"
              component={({ children }: any) => (
                <InputFeedback state="error" text={children} icon="exclamation-circle" />
              )}
            />
            <Field
              type="custom_text-letters-only"
              name="lastName"
              label={__('lastName') + ' *'}
              component={TextInput}
            />
            <ErrorMessage
              name="lastName"
              component={({ children }: any) => (
                <InputFeedback state="error" text={children} icon="exclamation-circle" />
              )}
            />
            <Field type="email" name="email" label={__('email') + ' *'} component={TextInput} />
            <ErrorMessage
              name="email"
              component={({ children }: any) => (
                <InputFeedback state="error" text={children} icon="exclamation-circle" />
              )}
            />
          </Form>
        </Modal>
      )}
    </Formik>
  );
};

export default StudentInformationModal;
